/**
 * StageTrajectories — admin-only aggregate view of relationship stage history.
 * Memory Plan v1, Phase 4. Mounted inside SessionDigests (admin tab=digests).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Loader2, AlertCircle, RefreshCw, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { authAxios } from "../../utils/auth";

const CONNECTION_STAGES = [
  "NONE", "SPARK", "OPENING", "RESONANCE", "SAFETY", "CURIOSITY",
  "ATTRACTION", "VULNERABILITY", "ALIGNMENT", "MOMENTUM",
  "INTIMACY", "INTEGRATION", "COMMITMENT", "UNION",
];

function stageLabel(stage) {
  if (!stage) return "—";
  const parts = stage.split("_");
  const idx = parseInt(parts[1], 10);
  return `${idx} · ${CONNECTION_STAGES[idx] || parts.slice(2).join(" ")}`;
}

function Sparkline({ points }) {
  if (!points || points.length === 0) {
    return <div className="text-white/30 text-xs">No data</div>;
  }
  const w = 220;
  const h = 36;
  const maxIdx = CONNECTION_STAGES.length - 1;
  const step = points.length > 1 ? w / (points.length - 1) : 0;
  const path = points
    .map((p, i) => {
      const y = h - (Math.max(0, p.stage_index ?? 0) / maxIdx) * h;
      return `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} className="block">
      <path d={path} fill="none" stroke="#C5A55A" strokeWidth="1.5" />
      {points.map((p, i) => {
        const y = h - (Math.max(0, p.stage_index ?? 0) / maxIdx) * h;
        const fill =
          p.change_type === "regression" ? "#f43f5e"
          : p.change_type === "progression" ? "#10b981"
          : "#C5A55A";
        return (
          <circle
            key={i}
            cx={(i * step).toFixed(1)}
            cy={y.toFixed(1)}
            r="2.5"
            fill={fill}
          />
        );
      })}
    </svg>
  );
}

export default function StageTrajectories() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState(90);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authAxios.get(
        `/admin/analytics/stage-trajectories?days=${windowDays}`
      );
      setData(res.data);
      setLastLoadedAt(new Date());
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || "Failed to load trajectories"
      );
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data
    ? [
        {
          label: "Relationships",
          value: data.relationship_count || 0,
          icon: null,
        },
        {
          label: "Progressions",
          value: data.progression_event_count || 0,
          icon: <TrendingUp className="w-3 h-3 text-emerald-400" />,
        },
        {
          label: "Regressions",
          value: data.regression_event_count || 0,
          icon: <TrendingDown className="w-3 h-3 text-rose-400" />,
        },
        {
          label: "Asynchronicities",
          value: data.asynchronicity_event_count || 0,
          icon: <AlertTriangle className="w-3 h-3 text-amber-400" />,
        },
      ]
    : [];

  return (
    <div
      data-testid="stage-trajectories"
      className="mt-8 border-t border-white/10 pt-6 space-y-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-divine-gold font-cinzel text-base">
            Stage trajectories
          </h3>
          <p className="text-white/40 text-xs">
            Aggregate stage_history across the last {windowDays} days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="stage-trajectories-window"
            value={windowDays}
            onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white"
          >
            <option value={30}>30d</option>
            <option value={60}>60d</option>
            <option value={90}>90d</option>
            <option value={180}>180d</option>
            <option value={365}>1y</option>
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-white/50 hover:text-divine-gold p-1 disabled:opacity-40"
            data-testid="stage-trajectories-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {lastLoadedAt ? (
        <div className="text-white/30 text-[10px]">
          Last refreshed {lastLoadedAt.toLocaleTimeString()}
        </div>
      ) : null}

      {error ? (
        <div className="flex items-start gap-2 p-3 text-rose-300 text-sm border border-rose-500/30 rounded">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      ) : null}

      {loading && !data ? (
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading trajectories…
        </div>
      ) : null}

      {data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {summary.map((s) => (
            <div
              key={s.label}
              className="border border-white/10 rounded-md p-3 bg-divine-black/40"
              data-testid={`stage-summary-${s.label.toLowerCase()}`}
            >
              <div className="text-white/40 text-[10px] uppercase tracking-wide flex items-center gap-1">
                {s.icon}
                {s.label}
              </div>
              <div className="text-divine-gold text-2xl font-semibold mt-1">
                {s.value}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {data && data.relationships?.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-divine-gold/80 text-xs uppercase tracking-wide">
            Per-relationship trajectories
          </h4>
          <ul className="space-y-2">
            {data.relationships.slice(0, 50).map((r) => (
              <li
                key={`${r.user_id}-${r.match_persona_id}`}
                data-testid={`stage-relationship-${r.match_persona_id}`}
                className="border border-white/5 rounded p-3 bg-divine-black/30 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">
                    {r.match_name}
                  </div>
                  <div className="text-white/40 text-[11px] truncate">
                    user={r.user_id?.slice(0, 14)}… · {r.point_count} point
                    {r.point_count === 1 ? "" : "s"} · current ={" "}
                    {stageLabel(r.points[r.points.length - 1]?.stage)}
                  </div>
                </div>
                <Sparkline points={r.points} />
              </li>
            ))}
          </ul>
        </div>
      ) : data && data.relationships?.length === 0 ? (
        <div
          className="text-white/40 text-sm border border-white/10 rounded p-3"
          data-testid="stage-trajectories-empty"
        >
          No stage history recorded in this window yet.
        </div>
      ) : null}
    </div>
  );
}
